import React, { useState, useRef, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

/**
 * LoopVideoBlock - 루프 재생되는 비디오 블록 컴포넌트
 * @param {object} props
 * @param {string} props.videoUrl - 비디오 URL
 * @param {boolean} [props.isMobile] - 모바일 여부
 * @param {number} [props.borderRadius] - 테두리 반경
 * @param {string} [props.aspectRatio] - 비디오 비율 (기본값: "1 / 1")
 * @param {string|number} [props.width] - 컨테이너 너비 (기본값: "100%")
 * @param {string|number} [props.height] - 컨테이너 높이 (aspectRatio 미사용시)
 * @param {object} [props.containerSx] - 컨테이너 추가 스타일
 * @param {boolean} [props.showError] - 에러 메시지 표시 여부 (기본값: true)
 * @param {string} [props.errorMessage] - 에러 메시지 (기본값: "Video unavailable")
 * @param {Function} [props.onLoadedData] - 비디오 로드 완료 콜백
 * @param {Function} [props.onError] - 비디오 에러 콜백
 * @param {boolean} [props.enableHover] - 호버 효과 활성화 (기본값: true)
 * @param {boolean} [props.playOnHover] - 호버 시에만 재생 (기본값: true)
 */
const LoopVideoBlock = ({
	videoUrl,
	isMobile,
	borderRadius,
	aspectRatio = "1 / 1",
	width = "100%",
	height,
	containerSx = {},
	showError = true,
	errorMessage = "Video unavailable",
	onLoadedData,
	onError,
	enableHover = true,
	playOnHover = true,
}) => {
	const theme = useTheme();
	const [videoLoaded, setVideoLoaded] = useState(false);
	const [videoError, setVideoError] = useState(false);
	const [isHovered, setIsHovered] = useState(false);
	const videoRef = useRef(null);
  const playPromiseRef = useRef(null);
  const enterTimerRef = useRef(null);
  const leaveTimerRef = useRef(null);

	const handleLoadedData = () => {
		setVideoLoaded(true);
		onLoadedData?.();
	};

	const handleError = () => {
		console.error(`Video failed to load: ${videoUrl}`);
		setVideoError(true);
		onError?.();
	};

  const handleMouseEnter = () => {
    if (!enableHover || isMobile || !playOnHover) return;
    setIsHovered(true);
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    if (enterTimerRef.current) clearTimeout(enterTimerRef.current);
    enterTimerRef.current = setTimeout(() => {
      const el = videoRef.current;
      if (!el) return;
      try {
        const p = el.play();
        if (p && typeof p.then === 'function') {
          playPromiseRef.current = p.catch(() => {});
        }
      } catch (_) {}
    }, 120);
  };

  const handleMouseLeave = () => {
    if (!enableHover || isMobile || !playOnHover) return;
    setIsHovered(false);
    if (enterTimerRef.current) {
      clearTimeout(enterTimerRef.current);
      enterTimerRef.current = null;
    }
    if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    leaveTimerRef.current = setTimeout(() => {
      const el = videoRef.current;
      if (!el) return;
      try {
        // 약간의 지연 후 일시정지하여 play()와 충돌 방지
        el.pause();
      } catch (_) {}
    }, 120);
  };

  useEffect(() => {
    return () => {
      if (enterTimerRef.current) clearTimeout(enterTimerRef.current);
      if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
      playPromiseRef.current = null;
    };
  }, []);

	if (!videoUrl) {
		return null;
	}

	const containerStyles = {
		width,
		...(height ? { height } : aspectRatio ? { aspectRatio } : {}),
		overflow: "hidden",
		borderRadius: borderRadius ?? (isMobile ? 1 : 2),
		position: "relative",
		...(enableHover && !isMobile
			? {
					cursor: "pointer",
			  }
			: {}),
		...containerSx,
	};

	return (
		<Box
			sx={containerStyles}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			{!videoError && (
				<video
					ref={videoRef}
					autoPlay={!playOnHover}
					loop
					muted
					playsInline
					preload="metadata"
					onLoadedData={handleLoadedData}
					onError={handleError}
					style={{
						width: "100%",
						height: "100%",
						objectFit: "cover",
						opacity: videoLoaded ? 1 : 0,
						transition: "opacity 0.5s ease-in-out",
					}}
					crossOrigin="anonymous"
				>
					<source src={videoUrl} type="video/mp4" />
					Your browser does not support the video tag.
				</video>
			)}
			{videoError && showError && (
				<Box
					sx={{
						width: "100%",
						height: "100%",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						backgroundColor: theme.palette.action.hover,
					}}
				>
					<Typography variant="body2" color="text.secondary">
						{errorMessage}
					</Typography>
				</Box>
			)}
		</Box>
	);
};

export default LoopVideoBlock;